import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Handshake, MessageCircle, Printer, ShieldCheck, Smartphone, Wallet } from 'lucide-react';
import { LOGO_LOCKUP_LIGHT } from '../lib/brand';

const WHAT_DUTILYNE_DOES = [
  'Point of sale, inventory, expiry and controlled-substance tracking for independent and multi-branch pharmacies.',
  'A connected patient portal for refill requests, order collection and adverse-reaction reporting.',
  'Offline-first operation at the till, so a dropped connection never stops a sale.',
  'Mobile Money billing (MTN, Telecel, AirtelTigo) so pharmacies pay the way they already do business.',
];

const WHY_IT_MATTERS = [
  { icon: ShieldCheck, title: 'Supports CPD & compliance record-keeping', body: 'Controlled-substance registers, adverse-reaction exports and audit trails give pharmacists a cleaner compliance record to show at inspection or renewal time.' },
  { icon: Smartphone, title: 'Digital-readiness without the heavy lift', body: 'No card machine or office PC required — DutiLyne\u2019s Staff Companion installs straight onto the Android phones most pharmacy staff already carry.' },
  { icon: Wallet, title: 'Local payments, local pricing', body: 'Subscriptions are priced in cedis and settled by Mobile Money — the same rails pharmacies already use for everyday business.' },
];

const PARTNERSHIP_IDEAS = [
  'A short briefing or demo slot at a PSGH branch meeting or Pharmacy Council CPD session.',
  'A co-branded introductory offer (extra free trial time) for members who sign up through the association.',
  'Shared learnings on digitisation from DutiLyne\u2019s pilot pharmacies, to complement the Council\u2019s own SEAPS/NePP work \u2014 not duplicate it.',
];

export default function PSGHPartnersPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="print-area min-h-screen bg-white text-slate-800" data-testid="psgh-partners-page">
      <div className="mx-auto max-w-3xl px-6 py-10 sm:px-10 sm:py-14">
        <div className="mb-8 flex items-center justify-between print:hidden">
          <Link to="/welcome" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-emerald-700" data-testid="psgh-back-link"><ArrowLeft size={16} /> Back to DutiLyne</Link>
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:border-emerald-600 hover:text-emerald-700" data-testid="psgh-print-button"><Printer size={15} /> Print / save as PDF</button>
        </div>

        <header className="mb-10 border-b border-slate-200 pb-8">
          <img src={LOGO_LOCKUP_LIGHT} alt="DutiLyne" className="h-9" data-testid="psgh-logo" />
          <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-emerald-700">For PSGH & Pharmacy Council of Ghana</p>
          <h1 className="mt-2 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Partnering with Ghana&rsquo;s pharmacy profession
          </h1>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            DutiLyne is a pharmacy management platform built for independent and multi-branch
            Ghanaian pharmacies. This page introduces what we do and how a partnership with the
            Pharmaceutical Society of Ghana (PSGH) or the Pharmacy Council of Ghana could work &mdash;
            alongside, not in place of, the Society&rsquo;s own SEAPS/API digitisation work and the
            Council&rsquo;s CPD and NePP initiatives.
          </p>
        </header>

        <section className="mb-10" data-testid="psgh-what-we-do">
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>What DutiLyne does</h2>
          <ul className="mt-4 space-y-3">
            {WHAT_DUTILYNE_DOES.map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed text-slate-600"><CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-600" />{item}</li>
            ))}
          </ul>
        </section>

        <section className="mb-10" data-testid="psgh-why-it-matters">
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Why it matters for members</h2>
          <div className="mt-4 space-y-5">
            {WHY_IT_MATTERS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex gap-4 rounded-lg border border-slate-200 p-4">
                <Icon size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                <div><strong className="block text-sm font-semibold text-slate-900">{title}</strong><p className="mt-1 text-sm leading-relaxed text-slate-600">{body}</p></div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10" data-testid="psgh-partnership-ideas">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}><Handshake size={19} className="text-emerald-600" /> How a partnership could work</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            We see this as a relationship, not a system integration. No access to SEAPS, NePP or
            any Council system is requested or required.
          </p>
          <ul className="mt-4 space-y-3">
            {PARTNERSHIP_IDEAS.map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed text-slate-600"><CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-600" />{item}</li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-6" data-testid="psgh-contact">
          <h2 className="text-base font-semibold text-emerald-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Let&rsquo;s talk</h2>
          <p className="mt-2 text-sm leading-relaxed text-emerald-800">
            We&rsquo;d welcome a short call or meeting slot to explore whether this is useful for
            your members.
          </p>
          <div className="mt-4 flex flex-wrap gap-3 print:hidden">
            <a href="https://wa.me/233242160467?text=Hello%2C%20I%27m%20reaching%20out%20from%20PSGH%2FPharmacy%20Council%20about%20a%20possible%20partnership%20with%20DutiLyne." target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700" data-testid="psgh-whatsapp-link"><MessageCircle size={16} /> WhatsApp +233 24 216 0467</a>
            <a href="mailto:info@dutilyne.com?subject=PSGH%2FPharmacy%20Council%20partnership" className="inline-flex items-center gap-2 rounded-full border border-emerald-600 px-5 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100" data-testid="psgh-email-link">info@dutilyne.com</a>
          </div>
          <p className="mt-4 hidden text-sm text-emerald-800 print:block">WhatsApp: +233 24 216 0467 &middot; Email: info@dutilyne.com</p>
        </section>

        <p className="mt-10 text-xs leading-relaxed text-slate-400" data-testid="psgh-disclaimer">
          DutiLyne is an independently operated pharmacy software platform. This document is a
          partnership proposal only and does not imply any existing endorsement, approval or
          technical integration with PSGH or the Pharmacy Council of Ghana unless formally agreed
          in writing.
        </p>
      </div>
    </div>
  );
}
